import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Music2, UtensilsCrossed, Bike, Zap } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

export default function Contact() {
  const { content } = useSiteContent();
  const c = content?.contact || {};

  return (
    <section id="visit" data-testid="contact-section" className="relative section-pad bg-bg-alt overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-6">
            <p className="text-xs tracking-[0.4em] uppercase text-[color:var(--gold-hi)]">{c.eyebrow}</p>
            <h2 className="mt-6 font-serif-display text-5xl md:text-7xl leading-[1.02] text-cream">
              {c.title_line_1} <span className="italic gold-ink">{c.title_line_2}</span>
            </h2>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="mt-12 space-y-8 max-w-md"
            >
              <div className="flex items-start gap-4">
                <MapPin className="text-[color:var(--gold-hi)] shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.35em] text-muted-warm">Address</div>
                  <div className="mt-1 text-cream font-light" data-testid="contact-address">{c.address}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone className="text-[color:var(--gold-hi)] shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.35em] text-muted-warm">Phone</div>
                  <a href={c.phone_link || `tel:${c.phone}`} data-testid="contact-phone" className="mt-1 block text-cream font-light hover:text-[color:var(--gold-hi)]">
                    {c.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="text-[color:var(--gold-hi)] shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.35em] text-muted-warm">Email</div>
                  <a href={`mailto:${c.email}`} data-testid="contact-email" className="mt-1 block text-cream font-light hover:text-[color:var(--gold-hi)]">
                    {c.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock className="text-[color:var(--gold-hi)] shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.35em] text-muted-warm">Hours</div>
                  <div className="mt-1 text-cream font-light" data-testid="contact-hours">{c.hours}</div>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <a href={c.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram"
                  className="w-11 h-11 rounded-full border border-warm flex items-center justify-center text-cream/85 hover:text-[color:var(--gold-hi)] hover:border-[color:var(--gold)] transition" aria-label="Instagram">
                  <Instagram size={18} />
                </a>
                <a href={c.facebook} target="_blank" rel="noreferrer" data-testid="contact-facebook"
                  className="w-11 h-11 rounded-full border border-warm flex items-center justify-center text-cream/85 hover:text-[color:var(--gold-hi)] hover:border-[color:var(--gold)] transition" aria-label="Facebook">
                  <Facebook size={18} />
                </a>
                {c.tiktok && (
                  <a href={c.tiktok} target="_blank" rel="noreferrer" data-testid="contact-tiktok"
                    className="w-11 h-11 rounded-full border border-warm flex items-center justify-center text-cream/85 hover:text-[color:var(--gold-hi)] hover:border-[color:var(--gold)] transition" aria-label="TikTok">
                    <Music2 size={18} />
                  </a>
                )}
                <a href={c.phone_link || `tel:${c.phone}`} data-testid="contact-reserve-btn"
                  className="ml-auto inline-flex items-center gap-2 rounded-full bg-[color:var(--gold)] text-[color:var(--bg)] px-6 py-3 text-sm tracking-wide uppercase font-medium hover:bg-[color:var(--gold-hi)] transition">
                  {c.reserve_label}
                </a>
              </div>

              {(c.uber_eats || c.glovo || c.bolt_food) && (
                <div className="pt-6">
                  <div className="text-xs uppercase tracking-[0.35em] text-muted-warm mb-3">Order In</div>
                  <div className="flex flex-wrap gap-2.5">
                    {c.uber_eats && (
                      <a href={c.uber_eats} target="_blank" rel="noreferrer" data-testid="contact-uber-eats"
                        className="inline-flex items-center gap-2 rounded-full border border-warm bg-bg-card/40 px-4 py-2.5 text-xs tracking-[0.15em] uppercase text-cream/90 hover:text-[color:var(--bg)] hover:bg-[color:var(--gold)] hover:border-[color:var(--gold)] transition">
                        <UtensilsCrossed size={14} /> Uber Eats
                      </a>
                    )}
                    {c.glovo && (
                      <a href={c.glovo} target="_blank" rel="noreferrer" data-testid="contact-glovo"
                        className="inline-flex items-center gap-2 rounded-full border border-warm bg-bg-card/40 px-4 py-2.5 text-xs tracking-[0.15em] uppercase text-cream/90 hover:text-[color:var(--bg)] hover:bg-[color:var(--gold)] hover:border-[color:var(--gold)] transition">
                        <Bike size={14} /> Glovo
                      </a>
                    )}
                    {c.bolt_food && (
                      <a href={c.bolt_food} target="_blank" rel="noreferrer" data-testid="contact-bolt-food"
                        className="inline-flex items-center gap-2 rounded-full border border-warm bg-bg-card/40 px-4 py-2.5 text-xs tracking-[0.15em] uppercase text-cream/90 hover:text-[color:var(--bg)] hover:bg-[color:var(--gold)] hover:border-[color:var(--gold)] transition">
                        <Zap size={14} /> Bolt Food
                      </a>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="md:col-span-6 relative"
          >
            <div className="aspect-square md:aspect-auto md:h-full rounded-2xl overflow-hidden ring-1 ring-warm bg-bg">
              <iframe
                title="Aseka's Mtaani Map"
                data-testid="contact-map"
                src={c.map_src}
                className="w-full h-full min-h-[420px] grayscale-[0.4] contrast-125"
                style={{ filter: "sepia(0.35) hue-rotate(-10deg) saturate(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
